import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, Pencil, Plus, Trash2, X } from "lucide-react";

import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { customerSegmentsService } from "@/services/customerSegments.service";
import { getErrorMessage } from "@/services/api";

/**
 * Manage the list of customer segments used across the platform.
 * Simple CRUD dialog reused wherever we need to configure the taxonomy.
 */
export function ManageSegmentsDialog({ open, onOpenChange }) {
    const qc = useQueryClient();
    const [newName, setNewName] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState("");
    const [deletingId, setDeletingId] = useState(null);

    const list = useQuery({
        queryKey: ["customer-segments", { includeInactive: true }],
        queryFn: () => customerSegmentsService.list({ includeInactive: true }),
        enabled: open,
    });

    const invalidate = () => {
        qc.invalidateQueries({ queryKey: ["customer-segments"] });
    };

    const createMut = useMutation({
        mutationFn: (payload) => customerSegmentsService.create(payload),
        onSuccess: () => { toast.success("Segmento adicionado."); setNewName(""); invalidate(); },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao criar segmento.")),
    });

    const patchMut = useMutation({
        mutationFn: ({ id, payload }) => customerSegmentsService.patch(id, payload),
        onSuccess: () => {
            toast.success("Segmento atualizado.");
            setEditingId(null); setEditingName("");
            invalidate();
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao atualizar segmento.")),
    });

    const removeMut = useMutation({
        mutationFn: (id) => customerSegmentsService.remove(id),
        onSuccess: () => { toast.success("Segmento removido."); setDeletingId(null); invalidate(); },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao remover segmento.")),
    });

    function handleCreate() {
        const name = (newName || "").trim();
        if (!name) return;
        createMut.mutate({ name });
    }

    function startEdit(seg) {
        setEditingId(seg.id);
        setEditingName(seg.name);
    }
    function confirmEdit() {
        const name = (editingName || "").trim();
        if (!name) return;
        patchMut.mutate({ id: editingId, payload: { name } });
    }
    function toggleActive(seg) {
        patchMut.mutate({ id: seg.id, payload: { active: !seg.active } });
    }

    const items = list.data?.items || [];

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="sm:max-w-lg" data-testid="manage-segments-dialog">
                    <DialogHeader>
                        <DialogTitle>Segmentos de clientes</DialogTitle>
                        <DialogDescription>
                            Adicione, renomeie ou remova segmentos usados no cadastro de clientes.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        {/* Add new */}
                        <form
                            className="flex gap-2"
                            onSubmit={(e) => { e.preventDefault(); handleCreate(); }}
                        >
                            <Input
                                placeholder="Novo segmento (ex.: Naval)"
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                data-testid="segments-new-name-input"
                                autoFocus
                            />
                            <Button
                                type="submit"
                                disabled={createMut.isPending || !newName.trim()}
                                data-testid="segments-create-button"
                            >
                                <Plus className="mr-2 h-4 w-4" />
                                Adicionar
                            </Button>
                        </form>

                        {/* List */}
                        <div className="rounded-lg border">
                            {list.isLoading ? (
                                <div className="p-3 space-y-2">
                                    {Array.from({ length: 4 }).map((_, i) => (
                                        <Skeleton key={`sk-seg-${i}`} className="h-8 w-full" />
                                    ))}
                                </div>
                            ) : items.length === 0 ? (
                                <p className="p-4 text-center text-sm text-muted-foreground">
                                    Nenhum segmento cadastrado.
                                </p>
                            ) : (
                                <ul className="divide-y">
                                    {items.map((seg) => {
                                        const isEditing = editingId === seg.id;
                                        return (
                                            <li
                                                key={seg.id}
                                                className="flex items-center gap-2 px-3 py-2"
                                                data-testid={`segment-row-${seg.id}`}
                                            >
                                                {isEditing ? (
                                                    <>
                                                        <Input
                                                            value={editingName}
                                                            onChange={(e) => setEditingName(e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter") { e.preventDefault(); confirmEdit(); }
                                                                if (e.key === "Escape") { setEditingId(null); setEditingName(""); }
                                                            }}
                                                            autoFocus
                                                            className="h-8"
                                                            data-testid={`segment-edit-input-${seg.id}`}
                                                        />
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={confirmEdit}
                                                            disabled={patchMut.isPending}
                                                            data-testid={`segment-save-${seg.id}`}
                                                        >
                                                            <Check className="h-4 w-4 text-emerald-600" />
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => { setEditingId(null); setEditingName(""); }}
                                                        >
                                                            <X className="h-4 w-4" />
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="flex-1 text-sm">{seg.name}</span>
                                                        {!seg.active && (
                                                            <Badge variant="outline" className="text-[10px]">
                                                                inativo
                                                            </Badge>
                                                        )}
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => toggleActive(seg)}
                                                            title={seg.active ? "Desativar" : "Ativar"}
                                                            data-testid={`segment-toggle-${seg.id}`}
                                                        >
                                                            <span className={`inline-block h-2 w-2 rounded-full ${seg.active ? "bg-emerald-500" : "bg-muted-foreground/50"}`} />
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => startEdit(seg)}
                                                            title="Renomear"
                                                            data-testid={`segment-edit-${seg.id}`}
                                                        >
                                                            <Pencil className="h-3.5 w-3.5" />
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => setDeletingId(seg.id)}
                                                            title="Remover"
                                                            data-testid={`segment-delete-${seg.id}`}
                                                        >
                                                            <Trash2 className="h-3.5 w-3.5 text-red-600" />
                                                        </Button>
                                                    </>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange?.(false)}
                            data-testid="segments-close-button"
                        >
                            Fechar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!deletingId} onOpenChange={(v) => !v && setDeletingId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remover segmento?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Os clientes já vinculados a este segmento manterão o nome registrado,
                            mas o segmento deixará de aparecer nos novos cadastros.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Manter</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); removeMut.mutate(deletingId); }}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            data-testid="segments-delete-confirm"
                        >
                            Remover
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
